import React, { useEffect, useState } from "react";

const BRAND_BLUE = "#1484F6";
const DIAL_CENTER = 120;
const DIAL_RADIUS = 96;

const pad = (n) => String(n).padStart(2, "0");

function polarToXY(angleDeg, radius, center) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: center + radius * Math.cos(rad), y: center + radius * Math.sin(rad) };
}

function parseTime(value) {
  if (!value) {
    const now = new Date();
    const h = now.getHours();
    let hour12 = h % 12;
    if (hour12 === 0) hour12 = 12;
    return { hour12, minute: 0, period: h >= 12 ? "PM" : "AM" };
  }
  const [hh, mm] = value.split(":").map(Number);
  let hour12 = hh % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minute: mm || 0, period: hh >= 12 ? "PM" : "AM" };
}

function to24Hour(hour12, period) {
  let h = hour12 % 12;
  if (period === "PM") h += 12;
  return h;
}

export function formatTime12(value) {
  if (!value) return "";
  const { hour12, minute, period } = parseTime(value);
  return `${hour12}:${pad(minute)} ${period}`;
}

const HOUR_NUMS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTE_NUMS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

/**
 * Custom circular time picker for mobile — replaces the native `<input type="time">`
 * so the AM/PM toggle can be fully styled (native pickers can't be recolored via CSS).
 */
export default function TimeWheelPicker({ open, value, onClose, onChange, allowClear = true }) {
  const [mode, setMode] = useState("hour");
  const [hour12, setHour12] = useState(12);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState("AM");

  useEffect(() => {
    if (open) {
      const p = parseTime(value);
      setHour12(p.hour12);
      setMinute(p.minute);
      setPeriod(p.period);
      setMode("hour");
    }
  }, [open, value]);

  if (!open) return null;

  const minuteMark = (Math.round(minute / 5) * 5) % 60;
  const nums = mode === "hour" ? HOUR_NUMS : MINUTE_NUMS;
  const activeVal = mode === "hour" ? hour12 : minuteMark;

  const pointFor = (val) => {
    const idxBase = mode === "hour" ? val % 12 : (val / 5) % 12;
    const angle = idxBase * 30 - 90;
    return polarToXY(angle, DIAL_RADIUS, DIAL_CENTER);
  };
  const selPoint = pointFor(activeVal);

  const handleSet = () => {
    const h24 = to24Hour(hour12, period);
    onChange(`${pad(h24)}:${pad(minute)}`);
    onClose();
  };
  const handleClear = () => {
    onChange("");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="time-picker-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[300px] rounded-2xl overflow-hidden shadow-2xl bg-white"
        data-testid="time-picker-dialog"
      >
        <div className="px-5 py-5 flex items-center justify-center gap-2.5" style={{ backgroundColor: BRAND_BLUE }}>
          <button
            type="button"
            onClick={() => setMode("hour")}
            data-testid="time-picker-hour-display"
            className="text-[42px] font-bold leading-none"
            style={{ color: mode === "hour" ? "#fff" : "rgba(255,255,255,0.55)" }}
          >
            {hour12}
          </button>
          <span className="text-[42px] font-bold leading-none text-white">:</span>
          <button
            type="button"
            onClick={() => setMode("minute")}
            data-testid="time-picker-minute-display"
            className="text-[42px] font-bold leading-none"
            style={{ color: mode === "minute" ? "#fff" : "rgba(255,255,255,0.55)" }}
          >
            {pad(minute)}
          </button>
          <div className="flex flex-col ml-1.5 rounded-md overflow-hidden border border-white/50">
            <button
              type="button"
              onClick={() => setPeriod("AM")}
              data-testid="time-picker-am-btn"
              className="px-2.5 py-1 text-[12.5px] font-bold transition-colors"
              style={period === "AM" ? { backgroundColor: BRAND_BLUE, color: "#fff" } : { backgroundColor: "#fff", color: "#1F2937" }}
            >
              AM
            </button>
            <button
              type="button"
              onClick={() => setPeriod("PM")}
              data-testid="time-picker-pm-btn"
              className="px-2.5 py-1 text-[12.5px] font-bold border-t border-white/50 transition-colors"
              style={period === "PM" ? { backgroundColor: BRAND_BLUE, color: "#fff" } : { backgroundColor: "#fff", color: "#1F2937" }}
            >
              PM
            </button>
          </div>
        </div>

        <div className="p-6 flex flex-col items-center">
          <div className="relative" style={{ width: DIAL_CENTER * 2, height: DIAL_CENTER * 2 }} data-testid="time-picker-dial">
            <div className="absolute inset-0 rounded-full" style={{ backgroundColor: "#EEF1F5" }} />
            <svg className="absolute inset-0 pointer-events-none" width={DIAL_CENTER * 2} height={DIAL_CENTER * 2}>
              <line x1={DIAL_CENTER} y1={DIAL_CENTER} x2={selPoint.x} y2={selPoint.y} stroke={BRAND_BLUE} strokeWidth="2" />
            </svg>
            <div
              className="absolute rounded-full pointer-events-none"
              style={{ left: DIAL_CENTER - 3, top: DIAL_CENTER - 3, width: 6, height: 6, backgroundColor: BRAND_BLUE }}
            />
            {nums.map((n) => {
              const { x, y } = pointFor(n);
              const isSel = n === activeVal;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    if (mode === "hour") {
                      setHour12(n);
                      setMode("minute");
                    } else {
                      setMinute(n);
                    }
                  }}
                  data-testid={`time-picker-${mode}-${n}`}
                  className="absolute flex items-center justify-center rounded-full text-[15px] font-semibold transition-colors active:scale-95"
                  style={{
                    left: x - 18,
                    top: y - 18,
                    width: 36,
                    height: 36,
                    backgroundColor: isSel ? BRAND_BLUE : "transparent",
                    color: isSel ? "#fff" : "#1F2937",
                  }}
                >
                  {mode === "hour" ? n : pad(n)}
                </button>
              );
            })}
          </div>

          <div className="w-full flex items-center justify-between mt-6">
            {allowClear ? (
              <button type="button" onClick={handleClear} data-testid="time-picker-clear-btn" className="text-[13.5px] font-bold" style={{ color: BRAND_BLUE }}>
                Clear
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-5">
              <button type="button" onClick={onClose} data-testid="time-picker-cancel-btn" className="text-[13.5px] font-bold" style={{ color: BRAND_BLUE }}>
                Cancel
              </button>
              <button type="button" onClick={handleSet} data-testid="time-picker-set-btn" className="text-[13.5px] font-bold" style={{ color: BRAND_BLUE }}>
                Set
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
